import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface StoryLockStatus {
  isLocked: boolean;
  isLockedByMe: boolean;
  lockedBy: string | null;
  lockedAt: string | null;
}

interface UseStoryLockReturn {
  lockStatus: StoryLockStatus;
  isAcquiringLock: boolean;
  acquireLock: () => Promise<boolean>;
  releaseLock: () => Promise<void>;
  refreshLock: () => Promise<void>;
}

const LOCK_REFRESH_INTERVAL = 2 * 60 * 1000; // 2 minutes
const LOCK_TIMEOUT = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to manage story locking for collaborative editing
 * Only activates when hasCollaborators is true
 */
export const useStoryLock = (
  storyId: string | undefined,
  hasCollaborators: boolean,
  onLockLost?: () => void
): UseStoryLockReturn => {
  const { toast } = useToast();
  const [lockStatus, setLockStatus] = useState<StoryLockStatus>({
    isLocked: false,
    isLockedByMe: false,
    lockedBy: null,
    lockedAt: null,
  });
  const [isAcquiringLock, setIsAcquiringLock] = useState(false);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  const refreshLockRef = useRef<() => Promise<void>>(async () => {});

  // Get current user ID
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      currentUserIdRef.current = user?.id || null;
    };
    getCurrentUser();
  }, []);

  // Check lock status
  const checkLockStatus = useCallback(async () => {
    if (!storyId || !hasCollaborators) return;

    try {
      const { data, error } = await supabase
        .from('stories')
        .select('locked_by, locked_at')
        .eq('id', storyId)
        .maybeSingle();

      if (error) {
        console.error('[useStoryLock] Error checking lock status:', error);
        return;
      }

      if (data) {
        const isLockedByMe = data.locked_by === currentUserIdRef.current;
        const isLocked = !!data.locked_by;

        setLockStatus({
          isLocked,
          isLockedByMe,
          lockedBy: data.locked_by,
          lockedAt: data.locked_at,
        });

        // If lock was lost, notify
        if (!isLockedByMe && isLocked && lockStatus.isLockedByMe) {
          onLockLost?.();
          toast({
            title: "Edit session ended",
            description: "Another user has started editing this story",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error('[useStoryLock] Exception checking lock:', error);
    }
  }, [storyId, hasCollaborators, lockStatus.isLockedByMe, onLockLost, toast]);

  // Acquire lock
  const acquireLock = useCallback(async (): Promise<boolean> => {
    if (!storyId || !hasCollaborators || !currentUserIdRef.current) {
      return true; // No locking needed
    }

    setIsAcquiringLock(true);
    try {
      const { data, error } = await supabase.rpc('acquire_story_lock', {
        _story_id: storyId,
        _user_id: currentUserIdRef.current,
      });

      if (error) {
        console.error('[useStoryLock] Error acquiring lock:', error);
        toast({
          title: "Unable to edit",
          description: "Failed to acquire edit lock. Please try again.",
          variant: "destructive",
        });
        return false;
      }

      if (data) {
        await checkLockStatus();

        // Start auto-refresh
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
        refreshIntervalRef.current = setInterval(() => {
          refreshLockRef.current();
        }, LOCK_REFRESH_INTERVAL);

        return true;
      } else {
        toast({
          title: "Story is locked",
          description: "Another user is currently editing this story",
          variant: "destructive",
        });
        return false;
      }
    } catch (error) {
      console.error('[useStoryLock] Exception acquiring lock:', error);
      return false;
    } finally {
      setIsAcquiringLock(false);
    }
  }, [storyId, hasCollaborators, checkLockStatus, toast]);

  // Refresh lock
  const refreshLock = useCallback(async () => {
    if (!storyId || !hasCollaborators || !currentUserIdRef.current || !lockStatus.isLockedByMe) {
      return;
    }

    try {
      const { error } = await supabase.rpc('acquire_story_lock', {
        _story_id: storyId,
        _user_id: currentUserIdRef.current,
      });

      if (error) {
        console.error('[useStoryLock] Error refreshing lock:', error);
      }
    } catch (error) {
      console.error('[useStoryLock] Exception refreshing lock:', error);
    }
  }, [storyId, hasCollaborators, lockStatus.isLockedByMe]);

  // Keep ref in sync for use inside setInterval
  refreshLockRef.current = refreshLock;

  // Release lock
  const releaseLock = useCallback(async () => {
    if (!storyId || !hasCollaborators || !currentUserIdRef.current) {
      return;
    }

    // Clear refresh interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    try {
      const { error } = await supabase.rpc('release_story_lock', {
        _story_id: storyId,
        _user_id: currentUserIdRef.current,
      });

      if (error) {
        console.error('[useStoryLock] Error releasing lock:', error);
      } else {
        setLockStatus({
          isLocked: false,
          isLockedByMe: false,
          lockedBy: null,
          lockedAt: null,
        });
      }
    } catch (error) {
      console.error('[useStoryLock] Exception releasing lock:', error);
    }
  }, [storyId, hasCollaborators]);

  // Subscribe to realtime lock changes
  useEffect(() => {
    if (!storyId || !hasCollaborators) return;

    const channel = supabase
      .channel(`story-lock-${storyId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'stories',
          filter: `id=eq.${storyId}`,
        },
        () => {
          checkLockStatus();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [storyId, hasCollaborators, checkLockStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      // Release lock on unmount
      if (lockStatus.isLockedByMe && storyId && hasCollaborators && currentUserIdRef.current) {
        supabase.rpc('release_story_lock', {
          _story_id: storyId,
          _user_id: currentUserIdRef.current,
        });
      }
    };
  }, [storyId, hasCollaborators, lockStatus.isLockedByMe]);

  return {
    lockStatus,
    isAcquiringLock,
    acquireLock,
    releaseLock,
    refreshLock,
  };
};
